<template>
    <v-container class="pa-5" fluid>
        <NotSentOrdersListDialog
            :is-open="notSentOrdersDialog"
            :items="notSentOrders"
            @on-close="closeNotSentOrderDialog"
        />
        <AddCommentOrderDialog
            :is-open="addCommentOrderDialog"
            :order-number="currentCommentOrderNumber"
            @on-close="closeAddCommentOrderDialog"
            @on-save="addCommentOrder"
        />
        <ConfirmDialog
            :is-open="showConfirmDialog"
            :data="confirmDialogData"
            @on-confirm="confirmDialogConfirm"
            @on-abort="confirmDialogClose"
        >
            <template #title>
                {{ confirmDialogData.title }}
            </template>
            <template #content>
                {{ confirmDialogData.content }}
            </template>
            <template #abortButton>
                {{ confirmDialogData.abortButton }}
            </template>
        </ConfirmDialog>
        <page-title>Lista zamówień WMD</page-title>

        <v-row align="center">
            <v-col cols="12">
                <v-container fluid class="pa-0">
                    <v-row justify="space-between" align="center" dense>
                        <v-col md="6" class="d-flex align-center">
                            <v-col class="text-left">
                                <v-btn
                                    :disabled="isSendOrderSelectedBlocked"
                                    :loading="sendOrderSelectedLoading"
                                    color="success"
                                    medium
                                    data-sentry-label="skierować do realizacji"
                                    @click="sendOrderSelected(orderSelected)"
                                >
                                    Do realizacji ({{ orderSelected.length }})
                                </v-btn>
                            </v-col>
                            <v-col
                                v-if="orderSelected.length > 30"
                                md="8"
                                class="text-left pa-2"
                            >
                                <v-alert
                                    dense
                                    outlined
                                    type="error"
                                    class="mb-0 ml-2"
                                >
                                    Można wybrać maksymalnie 30 zamówień.
                                </v-alert>
                            </v-col>
                            <v-col md="10">
                                <v-btn
                                    text
                                    color="red"
                                    :loading="ordersLoading || refreshLoading"
                                    @click="handleOrdersListRefresh"
                                >
                                    <v-icon left>
                                        mdi-autorenew
                                    </v-icon>
                                    Odśwież
                                </v-btn>
                            </v-col>
                        </v-col>
                        <v-col cols="auto">
                            <v-menu
                                v-model="filtersMenu"
                                top
                                left
                                transition="scroll-x-reverse-transition"
                                :close-on-content-click="false"
                            >
                                <template #activator="{on}">
                                    <v-btn
                                        fab
                                        :color="hasFilters ? 'secondary' : 'grey darken-1'"
                                        elevation="1"
                                        small
                                        v-on="on"
                                    >
                                        <v-icon v-if="hasFilters" color="white">
                                            mdi-filter-plus
                                        </v-icon>
                                        <v-icon v-else color="white">
                                            mdi-filter-minus
                                        </v-icon>
                                    </v-btn>
                                </template>
                                <v-card>
                                    <v-container fluid>
                                        <v-row justify="space-between" dense>
                                            <v-btn
                                                text
                                                color="primary"
                                                :disabled="!hasFilters"
                                                @click="clearFilters"
                                            >
                                                wyczyść filtry
                                            </v-btn>
                                            <v-col cols="auto">
                                                <v-btn
                                                    icon
                                                    small
                                                    @click="() => filtersMenu = false"
                                                >
                                                    <v-icon color="grey darken-2">
                                                        mdi-close
                                                    </v-icon>
                                                </v-btn>
                                            </v-col>
                                        </v-row>
                                        <v-row justify="start" dense>
                                            <v-col cols="12" md="3">
                                                <v-checkbox
                                                    v-model="filters.isComplaint"
                                                    label="Zamówienia reklamacyjne"
                                                    hide-details
                                                    class="mt-2"
                                                    @change="getOrders"
                                                />
                                            </v-col>
                                        </v-row>
                                        <v-row justify="end" dense>
                                            <v-col cols="12" md="4">
                                                <v-autocomplete
                                                    v-model="filters.productType_id"
                                                    label="Typ produktu"
                                                    :items="productTypesList"
                                                    :search-input.sync="searchProductTypesList"
                                                    item-value="id"
                                                    item-text="name"
                                                    no-data-text="Nie znaleziono typ produktu"
                                                    single-line
                                                    clearable
                                                    hide-details
                                                    solo
                                                    @change="changeFilter"
                                                    @click:clear="() => (searchProductTypesList = null) && clearFilter('productType_id')"
                                                />
                                            </v-col>
                                            <v-col cols="12" md="4">
                                                <v-text-field
                                                    v-model="filters.searchNumber"
                                                    append-icon="mdi-magnify"
                                                    label="Szukaj zamówienia..."
                                                    single-line
                                                    clearable
                                                    hide-details
                                                    solo
                                                    @keyup.enter="changeFilter"
                                                    @click:append="changeFilter"
                                                    @click:clear="clearFilter('searchNumber')"
                                                />
                                            </v-col>
                                            <v-col cols="12" md="4">
                                                <v-select
                                                    v-model="filters.status_id"
                                                    :items="deliveryStatusNames"
                                                    item-value="id"
                                                    item-text="name"
                                                    placeholder="Wybierz status..."
                                                    no-data-text="Brak danych..."
                                                    clearable
                                                    hide-details
                                                    solo
                                                    @change="() => showAll = false & changeFilter()"
                                                />
                                            </v-col>
                                        </v-row>
                                        <v-row justify="end" dense>
                                            <v-col cols="12" md="3">
                                                <v-menu
                                                    v-model="creationDateMenuFrom"
                                                    :close-on-content-click="false"
                                                    :nudge-right="40"
                                                    transition="scale-transition"
                                                    offset-y
                                                    min-width="290px"
                                                >
                                                    <template #activator="{on}">
                                                        <v-text-field
                                                            v-model="filters.createdAt__gte"
                                                            label="Data zamówienia od"
                                                            prepend-icon="mdi-calendar"
                                                            readonly
                                                            clearable
                                                            solo
                                                            hide-details
                                                            v-on="on"
                                                            @input="changeFilter"
                                                            @click:clear="clearFilter('createdAt__gte')"
                                                        />
                                                    </template>
                                                    <v-date-picker
                                                        v-model="filters.createdAt__gte"
                                                        first-day-of-week="1"
                                                        @change="changeFilter"
                                                        @input="creationDateMenuFrom = false"
                                                    />
                                                </v-menu>
                                            </v-col>
                                            <v-col cols="12" md="3">
                                                <v-menu
                                                    v-model="creationDateMenuTo"
                                                    :close-on-content-click="false"
                                                    :nudge-right="40"
                                                    transition="scale-transition"
                                                    offset-y
                                                    min-width="290px"
                                                >
                                                    <template #activator="{on}">
                                                        <v-text-field
                                                            v-model="filters.createdAt__lte"
                                                            label="Data zamówienia do"
                                                            prepend-icon="mdi-calendar"
                                                            readonly
                                                            clearable
                                                            solo
                                                            hide-details
                                                            v-on="on"
                                                            @input="changeFilter"
                                                            @click:clear="clearFilter('createdAt__lte')"
                                                        />
                                                    </template>
                                                    <v-date-picker
                                                        v-model="filters.createdAt__lte"
                                                        first-day-of-week="1"
                                                        @change="changeFilter"
                                                        @input="creationDateMenuTo = false"
                                                    />
                                                </v-menu>
                                            </v-col>
                                            <v-col cols="12" md="3">
                                                <v-menu
                                                    v-model="menuFrom"
                                                    :close-on-content-click="false"
                                                    :nudge-right="40"
                                                    transition="scale-transition"
                                                    offset-y
                                                    min-width="290px"
                                                >
                                                    <template #activator="{on}">
                                                        <v-text-field
                                                            v-model="filters.plannedSendDate__gte"
                                                            label="Wysyłka od"
                                                            prepend-icon="mdi-calendar"
                                                            readonly
                                                            clearable
                                                            solo
                                                            hide-details
                                                            v-on="on"
                                                            @input="changeFilter"
                                                            @click:clear="clearFilter('plannedSendDate__gte')"
                                                        />
                                                    </template>
                                                    <v-date-picker
                                                        v-model="filters.plannedSendDate__gte"
                                                        first-day-of-week="1"
                                                        @change="changeFilter"
                                                        @input="menuFrom = false"
                                                    />
                                                </v-menu>
                                            </v-col>
                                            <v-col cols="12" md="3">
                                                <v-menu
                                                    v-model="menuTo"
                                                    :close-on-content-click="false"
                                                    :nudge-right="40"
                                                    transition="scale-transition"
                                                    offset-y
                                                    min-width="290px"
                                                >
                                                    <template #activator="{on}">
                                                        <v-text-field
                                                            v-model="filters.plannedSendDate__lte"
                                                            label="Wysyłka do"
                                                            prepend-icon="mdi-calendar"
                                                            readonly
                                                            clearable
                                                            solo
                                                            hide-details
                                                            v-on="on"
                                                            @input="changeFilter"
                                                            @click:clear="clearFilter('plannedSendDate__lte')"
                                                        />
                                                    </template>
                                                    <v-date-picker
                                                        v-model="filters.plannedSendDate__lte"
                                                        first-day-of-week="1"
                                                        @change="changeFilter"
                                                        @input="menuTo = false"
                                                    />
                                                </v-menu>
                                            </v-col>
                                        </v-row>
                                    </v-container>
                                </v-card>
                            </v-menu>
                        </v-col>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-card>
                    <v-data-table
                        v-model="orderSelected"
                        :headers="headers"
                        :items="orders"
                        show-select
                        :loading="ordersLoading || refreshLoading"
                        :footer-props="footerProps"
                        :server-items-length="pagination.itemsLength"
                        :page.sync="pagination.page"
                        :items-per-page.sync="pagination.itemsPerPage"
                        :sort-by.sync="sorting"
                        :sort-desc.sync="sortingDir"
                        :mobile-breakpoint="0"
                        :item-class="setSelectedRow"
                        name="ordersData"
                        must-sort
                        @pagination="updatePagination"
                        @update:sort-desc="customSort"
                        @toggle-select-all="selectAllRowsToggle"
                    >
                        <template #item.data-table-select="{item, isSelected, select}">
                            <v-simple-checkbox
                                :value="isSelected"
                                :disabled="item.status.id === 120/** if cancelled */"
                                :ripple="false"
                                @input="select($event)"
                            />
                        </template>
                        <template #item.fullProductionNumber="{item}">
                            <div class="d-flex align-center">
                                <span>{{ item.fullProductionNumber }}</span>
                                <v-tooltip v-if="hasPreflightMessages(item)" :open-delay="tooltipDelay" right>
                                    <template #activator="{on}">
                                        <v-icon color="info" class="ml-2" outlined v-on="on">
                                            mdi-alpha-p-circle-outline
                                        </v-icon>
                                    </template>
                                    <div class="text-left py-2">
                                        <div v-for="component in getComponentsWithMessages(item)" :key="component.id" class="mb-2">
                                            <div class="font-weight-bold">
                                                {{ component.component.name }}:
                                            </div>
                                            <ul class="pl-4 mb-0 preflight-messages-list">
                                                <li v-for="(msg, index) in component.preflightMessages" :key="index">
                                                    {{ msg.content }}
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </v-tooltip>
                            </div>
                        </template>
                        <template #item.patterLayerPresent="{item}">
                            <v-tooltip v-if="item.patterLayerPresent === false" :open-delay="tooltipDelay" top>
                                <template #activator="{on}">
                                    <span
                                        icon
                                        data-sentry-label="Brak wymaganej makiety"
                                        v-on="on"
                                    >
                                        <v-icon color="error">
                                            mdi-alert-circle-outline
                                        </v-icon>
                                    </span>
                                </template>
                                <span>Brak wymaganej makiety</span>
                            </v-tooltip>
                        </template>
                        <template #item.hasMissingTranslations="{item}">
                            <v-icon v-if="item.hasMissingTranslations === true" color="error">
                                mdi-translate-off
                            </v-icon>
                        </template>
                        <template #item.hasMissingPreviews="{item}">
                            <v-icon v-if="item.hasMissingPreviews === true" color="error">
                                mdi-file-find
                            </v-icon>
                        </template>
                        <template #item.createdAt="{item}">
                            {{ formatDate(item.createdAt) }}
                        </template>
                        <template #item.plannedSendDate="{item}">
                            {{ formatDate(item.plannedSendDate, false) }}
                        </template>
                        <template #item.plannedDeliveryDate="{item}">
                            {{ formatDate(item.plannedDeliveryDate, false) }}
                        </template>
                        <template #item.shipmentStatus="{item}">
                            {{ item.status.name }}

                            <v-tooltip v-if="item.overdue" :open-delay="tooltipDelay" top>
                                <template #activator="{on}">
                                    <v-icon color="warning" small v-on="on">
                                        mdi-clock-outline
                                    </v-icon>
                                </template>
                                <span>Opóźnione</span>
                            </v-tooltip>
                        </template>
                        <template #item.actions="{item}">
                            <div class="d-inline-flex">
                                <v-tooltip :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <div v-on="on">
                                            <v-btn
                                                icon
                                                data-sentry-label="Złóż zamówienie"
                                                :disabled="!canBeSubmitted(item)"
                                                @click="submitOrder(item.id)"
                                            >
                                                <v-icon color="teal">
                                                    mdi-cart-outline
                                                </v-icon>
                                            </v-btn>
                                        </div>
                                    </template>
                                    <span>Złóż zamówienie</span>
                                </v-tooltip>
                                <v-tooltip :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <div v-on="on">
                                            <v-btn
                                                icon
                                                data-sentry-label="Anuluj zamówienie"
                                                :disabled="!canBeCanceled(item)"
                                                @click="cancelOrderHandler(item.id)"
                                            >
                                                <v-icon color="pink">
                                                    mdi-file-remove-outline
                                                </v-icon>
                                            </v-btn>
                                        </div>
                                    </template>
                                    <span>Anuluj zamówienie</span>
                                </v-tooltip>
                                <v-tooltip :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <a
                                            :href="$router.resolve({
                                                name: 'ordersWMDOrdersOrder',
                                                params: {id: item.id}
                                            }).href"
                                        >
                                            <v-btn
                                                icon
                                                data-sentry-label="Otwórz"
                                                v-on="on"
                                                @click="openOrder(item.id)"
                                            >
                                                <v-icon color="blue">
                                                    mdi-ballot
                                                </v-icon>
                                            </v-btn>
                                        </a>
                                    </template>
                                    <span>Otwórz</span>
                                </v-tooltip>
                                <v-tooltip :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <v-btn
                                            icon
                                            :disabled="!canAddComment(item)"
                                            data-sentry-label="Dodaj kommentarż"
                                            v-on="on"
                                            @click="openAddCommentOrderDialog(item)"
                                        >
                                            <v-icon color="green">
                                                mdi-alpha-k-box
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Dodaj kommentarż</span>
                                </v-tooltip>
                                <v-tooltip :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <div v-on="on">
                                            <v-btn
                                                icon
                                                :disabled="!isNewOrder(item)"
                                                data-sentry-label="Odśwież zamówienie"
                                                v-on="on"
                                                @click="tryReloadOrder(item.id)"
                                            >
                                                <v-icon color="blue">
                                                    mdi-reload
                                                </v-icon>
                                            </v-btn>
                                        </div>
                                    </template>
                                    <span>Odśwież zamówienie</span>
                                </v-tooltip>
                                <v-tooltip :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <v-btn
                                            icon
                                            :disabled="!canRemove(item)"
                                            data-sentry-label="Usuń"
                                            v-on="on"
                                            @click="tryRemoveOrder(item.id)"
                                        >
                                            <v-icon color="pink">
                                                mdi-delete
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Usuń</span>
                                </v-tooltip>
                            </div>
                        </template>
                    </v-data-table>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import router from '@/router'
import PageTitle from '@/components/common/PageTitle'
import filters from '@/mixins/dataTableFilters'
import pagination from '@/mixins/dataTablePagination'
import tooltipDelay from '@/mixins/tooltip'
import dateFormatter from '@/mixins/dateFormatter'
import dataTableClassSetter from '@/mixins/dataTableClassSetter'
import NotSentOrdersListDialog from '../Common/NotSentOrdersListDialog.vue'
import AddCommentOrderDialog from '../Common/AddCommentOrderDialog.vue'
import ConfirmDialog from '@/components/common/ConfirmDialog'
import { errorHandler } from '@/utils/errorHandler'


export default {
    name: 'WMDOrders',
    components: {
        PageTitle,
        NotSentOrdersListDialog,
        AddCommentOrderDialog,
        ConfirmDialog,
    },
    mixins: [
        tooltipDelay,
        filters,
        pagination,
        dateFormatter,
        dataTableClassSetter
    ],
    beforeRouteLeave(to, from, next) {
        if (!this.filtersCleared) {
            this.$store.dispatch('orders/clearWMDOrdersFilters')
            this.clearSelectedListItemId()
        }
        this.resetFiltersCleared()
        next()
    },
    data() {
        return {
            filtersMenu: false,
            creationDateMenuFrom: false,
            creationDateMenuTo: false,
            menuFrom: false,
            menuTo: false,
            showAll: false,
            sorting: ['createdAt'],
            sortingDir: [true],
            refreshLoading: false,
            orderSelected: [],
            notSentOrders: [],
            notSentOrdersDialog: false,
            sendOrderSelectedLoading: false,
            searchProductTypesList: null,
            addCommentOrderDialog: false,
            currentCommentOrder: null,
            headers: [
                {
                    value: 'patterLayerPresent',
                    sortable: false,
                    width: 40
                },
                {
                    value: 'hasMissingTranslations',
                    sortable: false,
                    width: 40
                },
                {
                    value: 'hasMissingPreviews',
                    sortable: false,
                    width: 40
                },
                {
                    text: 'Numer Klienta',
                    value: 'shopOrderNumber',
                    sortable: false,
                    width: 200
                },
                {
                    text: 'Numer zamówienia',
                    value: 'fullProductionNumber',
                    sortable: false,
                    width: 200
                },
                {
                    text: 'Nazwa produktu',
                    value: 'shopProductName',
                    sortable: false,
                    align: 'left'
                },
                {
                    text: 'Nakład',
                    value: 'quantity',
                    sortable: false,
                    align: 'center'
                },
                {
                    text: 'Data zamówienia',
                    value: 'createdAt',
                    sortable: true,
                    align: 'center'
                },
                {
                    text: 'Planowana data wysyłki',
                    value: 'plannedSendDate',
                    sortable: false,
                    align: 'center'
                },
                {
                    text: 'Data dostawy',
                    value: 'plannedDeliveryDate',
                    sortable: false,
                    align: 'center'
                },
                {
                    text: 'Status',
                    value: 'shipmentStatus',
                    sortable: false,
                    align: 'center'
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    sortable: false,
                    align: 'center',
                    width: 100
                }
            ],
            showConfirmDialog: false,
            confirmDialogData: {
                confirm: () => {},
            },
        }
    },
    computed: {
        orders() {
            return this.$store.state.orders.wmdOrder.orders
        },
        productTypesList() {
            return this.$store.state.configurations.products.products
        },
        deliveryStatusNames() {
            return this.$store.state.orders.order.orderStatusList
        },
        ordersLoading() {
            return this.$store.state.orders.wmdOrder.ordersLoading
        },
        savedFilters() {
            return this.$store.state.orders.wmdOrder.filters
        },
        selectedOrderId() {
            return this.$store.state.common.selectedListItemId
        },
        isSendOrderSelectedBlocked() {
            return this.orderSelected.length === 0 || this.orderSelected.length > 30
        },
        hasFilters() {
            let filters = Object.fromEntries(
                Object.entries(this.filters).filter(([key]) => key !== 'ordering')
            )

            return !Object.values(filters).every((item) => item == null)
        },
        disabledOrdersCount() {
            return this.orders.reduce((count, order) => {
                return [120, 130].includes(order.status.id) ? ++count : count
            }, 0)
        },
        filterQuery() {
            const filters = Object.assign({}, this.filters)
            let query = ''

            for (let item in filters) {
                if (filters[item] != null && filters[item] != '') {
                    if (item == 'createdAt__gte') {
                        filters[item] += 'T00:00:00Z'
                    }
                    if (item == 'createdAt__lte') {
                        filters[item] += 'T23:59:59Z'
                    }

                    query = query.concat('&', `${item}=${filters[item]}`)
                }
            }

            return query
        },
        currentCommentOrderNumber() {
            return this.currentCommentOrder?.fullProductionNumber
        },
        userName() {
            return this.$store.state.user.userName
        },
    },
    beforeMount() {
        if (!this.savedPagination.pagination) {
            this.pagination.itemsPerPage = 50
            this.resetPagination()
        }
    },
    created() {
        this.$store.dispatch('orders/getOrderStatuses')
        this.$store.dispatch('configurations/getProducts')
    },
    mounted() {
        this.getOrders()
    },
    methods: {
        openOrderInNewTab(orderId) {
            const route = this.$router.resolve({
                name: 'ordersWMDOrdersOrder',
                params: {
                    id: orderId,
                }
            })
            
            window.open(route.href, '_blank')
        },
        customSort() {
            this.filters.ordering = (this.sortingDir.at(0) ? '-' : '') + this.sorting.at(0)
            this.changeFilter()
        },
        submitOrder(orderId) {
            this.$store.dispatch('orders/acceptedWMDOrder', orderId)
                .then(() => {
                    this.getOrders()
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        sendOrderSelected(orders) {
            let orderIds = orders.map(o => o.id)

            this.sendOrderSelectedLoading = true
            this.$store.dispatch('orders/sendToProductionWMDOrder', { orderIds: orderIds })
                .then(({ data }) => {
                    if (data.notSentOrders.length > 0) {
                        this.notSentOrders = this.orderSelected.filter(item => data.notSentOrders.includes(item.fullProductionNumber))
                        this.notSentOrdersDialog = true
                    }
                    this.getOrders()
                })
                .finally(() => {
                    this.sendOrderSelectedLoading = false
                    this.orderSelected = []
                })
        },
        cancelOrderHandler(orderId) {
            this.$store.dispatch('orders/canceledWMDOrder', orderId)
                .then(() => {
                    this.getOrders()
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        tryRemoveOrder(orderId) {
            this.confirmDialogData = {
                title: 'Usuwanie zamówienia',
                content: 'Czy na pewno chcesz usunąć zamówienie?',
                abortButton: 'nie',
                confirm: this.removeOrderHandler,
                data: orderId,
            }
            this.showConfirmDialog = true
        },
        tryReloadOrder(orderId) {
            this.confirmDialogData = {
                title: 'Odświeżanie zamówienia',
                content: 'Czy na pewno chcesz wgrać zamówienie ponownie?',
                abortButton: 'nie',
                confirm: this.deleteOrderHandler,
                data: orderId,
            }
            this.showConfirmDialog = true
        },
        confirmDialogConfirm(payload) {
            this.confirmDialogData.confirm(payload)
            this.confirmDialogClose()
        },
        confirmDialogClose() {
            this.showConfirmDialog = false
            setTimeout(() => {
                this.confirmDialogData = {
                    title: null,
                    content: null,
                    abortButton: 'Anuluj',
                    confirm: () => {},
                    data: null,
                }
            }, 500)
        },
        removeOrderHandler({ data }) {
            this.$store.dispatch('orders/removeWMDOrder', data)
                .then(() => this.getOrders())
                .catch(error => errorHandler(error))
        },
        deleteOrderHandler({ data }) {
            const index = this.orders.findIndex(order => order.id === data)

            this.orders.splice(index, 1)
            this.$store.dispatch('orders/deleteWMDOrder', data)
                .then(() => {
                    this.handleOrdersListRefresh()
                })
                .catch(error => {errorHandler(error)})
        },
        openAddCommentOrderDialog(order) {
            this.addCommentOrderDialog = true
            this.currentCommentOrder = order
        },
        addCommentOrder(comment) {
            if (this.currentCommentOrder) {
                this.$store.dispatch('orders/addCommentWMD', {
                    comment: comment,
                    username: this.userName,
                    orderId: this.currentCommentOrder.id
                })
                    .catch(error => {
                        errorHandler(error)
                    })
            }
        },
        closeAddCommentOrderDialog() {
            this.addCommentOrderDialog = false
            this.currentCommentOrder = null
        },
        setSelectedRow(item) {
            if ([120, 130].includes(item.status.id)) {
                return 'text--disabled'
            }
            if (item.id === this.selectedOrderId) {
                return 'isSelected'
            }
        },
        selectAllRowsToggle() {
            if (this.orderSelected.length != this.orders.length - this.disabledOrdersCount) {
                this.orders.forEach(order => {
                    if (![120, 130].includes(order.status.id)) {
                        this.orderSelected.push(order)
                    }
                })
            } else {
                this.orderSelected = []
            }
        },
        handleOrdersListRefresh() {
            this.refreshLoading = true
            this.$store.dispatch('orders/refreshWMDOrders')
                .then(() => {
                    this.getOrders()
                    this.refreshLoading = false
                })
        },
        getOrders() {
            this.$store.dispatch('orders/getWMDOrders', {
                paginate: this.paginate(),
                query: this.filterQuery
            })
                .then(response => {
                    this.handlePaginationResponse(response)
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        openOrder(orderId) {
            this.savePagination(this.$options.name)
            this.saveFiltersCleared()
            this.setSelectedListItemId(orderId)
            router.push({
                name: 'ordersWMDOrdersOrder',
                params: {
                    id: orderId,
                }
            })
                .catch(error => {
                    errorHandler(error)
                })
        },
        clearFilters() {
            Object.entries(this.filters)
                .forEach(([key])=> {
                    this.filters[key] = null
                })
            this.changeFilter()
            this.filtersMenu = false
        },
        changeFilter() {
            this.$store.dispatch('orders/setWMDOrdersFilters', this.filters)
            this.resetPagination()
            this.clearSelected()
            this.getOrders()
        },
        clearSelected() {
            this.orderSelected = []
        },
        canBeSubmitted(item) {
            return ![13, 15, 110, 120, 130].includes(item.status.id)
        },
        canBeCanceled(item) {
            return ![10, 13, 120, 130].includes(item.status.id)
        },
        canAddComment(item) {
            return ![13, 130].includes(item.status.id)
        },
        isNewOrder(item) {
            return item.status.id === 10
        },
        canRemove(item) {
            return this.isNewOrder(item)
        },
        updatePagination(pagination) {
            this.pagination = pagination
            this.evaluateSavedPagination(this.$options.name)
            if (pagination.pageCount !== 0 && !this.paginationSettings.paginationPending && !this.savedPagination.pagination) {
                this.getOrders()
            }
            this.clearPaginationPending()
        },
        closeNotSentOrderDialog() {
            this.notSentOrdersDialog = false
        },
        getComponentsWithMessages(item) {
            const components = []

            if (!item.orderProducts) return components

            item.orderProducts.forEach(orderProduct => {
                if (orderProduct.orderProductComponents) {
                    orderProduct.orderProductComponents.forEach(component => {
                        if (component.preflightMessages && component.preflightMessages.length > 0) {
                            components.push(component)
                        }
                    })
                }
            })

            return components
        },
        hasPreflightMessages(item) {
            return item.orderProducts?.some(op =>
                op.orderProductComponents?.some(c => c.preflightMessages?.length > 0)
            ) ?? false
        }
    }
}
</script>

<style scoped>
.preflight-messages-list {
    list-style-type: disc;
}
</style>
